import { createContextFromImprCodingCtxHeader } from '@imprivata-cloud/dataprivacy';
import type { CodingContext } from '@imprivata-cloud/dataprivacy';
import { debug } from '../utils/logging';

export const CODING_CONTEXT_STORAGE_KEY = 'codingContext';

/**
 * The Epic OAuth redirect wipes in-memory state. sessionStorage hands the
 * same coding context the backend saved in the session back to the UI after
 * the /enroll-success (or /enroll-failure) redirect.
 */
export function storeCodingContext(codingContext: CodingContext): void {
  sessionStorage.setItem(
    CODING_CONTEXT_STORAGE_KEY,
    codingContext.buildImprCodingCtxHeader(),
  );
}

/**
 * Reads and removes the stored coding context in one step, so it never
 * survives past a single use. A malformed/undecodable value is treated as
 * "no context" rather than thrown.
 */
export function takeStoredCodingContext(): CodingContext | undefined {
  const storedCodingContext = sessionStorage.getItem(
    CODING_CONTEXT_STORAGE_KEY,
  );
  sessionStorage.removeItem(CODING_CONTEXT_STORAGE_KEY);

  if (!storedCodingContext) {
    return undefined;
  }

  try {
    return createContextFromImprCodingCtxHeader(storedCodingContext);
  } catch (error) {
    debug(
      '[takeStoredCodingContext] Invalid stored coding context',
      JSON.stringify({ error }),
    );
    return undefined;
  }
}

/**
 * Older builds wrote the redirect hand-off to localStorage (this moved to
 * sessionStorage). Migrate rather than drop it, so an Epic OAuth round-trip
 * that started under an old bundle and returns under a new one (a
 * deployment straddling the redirect) still finds its context. Safe to
 * remove after a release or two once devices have picked up the new build.
 */
export function migrateLegacyCodingContext(): void {
  const legacyCodingContext = localStorage.getItem(CODING_CONTEXT_STORAGE_KEY);

  if (
    legacyCodingContext &&
    !sessionStorage.getItem(CODING_CONTEXT_STORAGE_KEY)
  ) {
    sessionStorage.setItem(CODING_CONTEXT_STORAGE_KEY, legacyCodingContext);
  }

  localStorage.removeItem(CODING_CONTEXT_STORAGE_KEY);
}
